import React, { memo, useMemo } from 'react';

import { useImageSourceProps } from '../hooks';
import { Image } from '../Image';
import { type ImageProps } from '../Image.types';
import { getCloudinaryUrl } from './getCloudinaryUrl';

// ────────────────────────────────────────────────────────────────────────────────

export const CloudinaryImage = memo((props: CloudinaryImageProps) => {
  const { baseUrl, staticBaseUrl, config, ...rest } = props;

  // ─── Derived Data ────────────────────────────────────────────────────

  const transformedSource = useMemo(
    () => getCloudinaryUrl(baseUrl, config),
    [baseUrl, config],
  );

  const transformedStaticSource = useMemo(
    () => (staticBaseUrl ? getCloudinaryUrl(staticBaseUrl, config) : undefined),
    [staticBaseUrl, config],
  );

  const imageSourceProps = useImageSourceProps({
    source: transformedSource,
    staticSourceUrl: transformedStaticSource,
  });

  // ─────────────────────────────────────────────────────────────────────

  return <Image {...imageSourceProps} {...rest} />;
});

// ─── Types ──────────────────────────────────────────────────────────────────────

type CloudinaryImageProps = Readonly<{
  baseUrl: string;

  /**
   * A URL to the optional static image that can be used for a11y purposes to
   * swap animated image with a static version based on the "reduce motion" mode
   * on native platform or the "static source" attribute on the web.
   *
   * @see {@link useStaticImageProps}
   */
  staticBaseUrl?: string;

  config: CloudinaryTransformConfig;
}> &
  Omit<ImageProps, 'source'>;

export type CloudinaryTransformConfig = Parameters<typeof getCloudinaryUrl>[1];
